import { inject, Injectable } from '@angular/core';

import { CmsNavigationComponent, CmsNavigationNode, CmsService, SemanticPathService } from '@spartacus/core';
import { NavigationNode } from '@spartacus/storefront';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { DpNavigationCategoriesService } from './navigation-categories.service';

@Injectable({
  providedIn: 'root'
})
/* eslint-disable */
export class DpNavigationService {
  // @ts-nocheck

  #navigationCategoriesService = inject(DpNavigationCategoriesService);

  constructor(
    protected cmsService: CmsService,
    protected semanticPathService: SemanticPathService
  ) {}

  createNavigation(data$: Observable<CmsNavigationComponent>): Observable<NavigationNode> {
    return combineLatest([
      this.getNavigationNode(data$),
      this.#navigationCategoriesService.getNavigationProductCategories()
    ]).pipe(map((result) => this.#getUpdatedNavigationTree(...result)));
  }

  getNavigationNode(data$: Observable<CmsNavigationComponent>): Observable<NavigationNode> {
    if (!data$) {
      return of();
    }

    return data$.pipe(
      filter((data) => !!data),
      switchMap((data) => {
        const navigation = data.navigationNode ? data.navigationNode : data;

        return this.cmsService.getNavigationEntryItems(navigation.uid ?? '').pipe(
          tap((items) => {
            if (items === undefined) {
              this.#loadNavigationEntryItems(navigation, true);
            } else {
              // we should check whether the existing node items are what expected
              // @ts-ignore
              const expectedItems = [];
              // @ts-ignore
              this.#loadNavigationEntryItems(navigation, false, expectedItems);
              const existingItems = Object.keys(items).map((key) => items[key].uid);
              // @ts-ignore
              const missingItems = expectedItems.filter((it) => !existingItems.includes(it.id));
              if (missingItems.length > 0) {
                // @ts-ignore
                this.cmsService.loadNavigationItems(navigation.uid, missingItems);
              }
            }
          }),
          filter(Boolean),
          map((items) => this.#populateNavigationNode(navigation, items))
        );
      })
    );
  }

  // eslint-disable-next-line
  #loadNavigationEntryItems(
    nodeData: CmsNavigationNode,
    root: boolean,
    itemsList: { superType: string | undefined; id: string | undefined }[] = []
  ): void {
    if (nodeData.entries && nodeData.entries.length > 0) {
      // @ts-ignore
      nodeData.entries.forEach((entry) => {
        itemsList.push({
          // @ts-ignore
          superType: entry.itemSuperType,
          // @ts-ignore
          id: entry.itemId
        });
      });
    }

    if (nodeData.children && nodeData.children.length > 0) {
      // @ts-ignore
      nodeData.children.forEach((child) => this.#loadNavigationEntryItems(child, false, itemsList));
    }

    if (root) {
      this.cmsService.loadNavigationItems(nodeData.uid as string, itemsList);
    }
  }

  // eslint-disable-next-line
  #populateNavigationNode(nodeData: any, items: any): NavigationNode {
    const node: NavigationNode = {};

    if (nodeData.uid) {
      node.uid = nodeData.uid;
    }

    if (nodeData.title) {
      // the node title will be populated by the first entry (if any)
      // if there's no nodeData.title available
      node.title = nodeData.title;
    }

    // populate style classes to apply CMS driven styling
    if (nodeData.styleClasses) {
      node.styleClasses = nodeData.styleClasses;
    }
    // populate style attributes to apply CMS driven styling
    if (nodeData.styleAttributes) {
      node.styleAttributes = nodeData.styleAttributes;
    }

    if (nodeData.entries && nodeData.entries.length > 0) {
      this.#populateLink(node, nodeData.entries[0], items);
    }

    if (nodeData.children?.length > 0) {
      const children = nodeData.children
        // @ts-ignore
        .map((child) => this.#populateNavigationNode(child, items))
        .filter(Boolean);

      if (children.length > 0) {
        node.children = children;
      }
    }

    // return null in case there are no children
    // @ts-ignore
    return Object.keys(node).length === 0 ? undefined : node;
  }

  // @ts-ignore
  #populateLink(node: NavigationNode, entry, items) {
    const item = items[`${entry.itemId}_${entry.itemSuperType}`];

    // now we only consider CMSLinkComponent
    if ((item && entry.itemType === 'CMSLinkComponent') || entry.itemType === 'JavascriptLinkComponent') {
      if (!node.title) {
        node.title = item.linkName;
      }
      const url = this.#getLink(item);

      // only populate the node link if we have a visible node
      if (node.title && url) {
        node.url = url;
        // the backend provide boolean value for the target
        // in case the link should be opened in a new window
        if (item.target === 'true' || item.target === true) {
          node.target = '_blank';
        }
      }

      if (!node.localizedUrl) {
        node.localizedUrl = item.localizedUrl;
      }

      if (item.javaScript) {
        node.javaScript = item.javaScript;
      }
      // populate style classes to apply CMS driven styling
      if (item.styleClasses) {
        node.styleClasses = item.styleClasses;
      }
      // populate style attributes to apply CMS driven styling
      if (item.styleAttributes) {
        node.styleAttributes = item.styleAttributes;
      }
    }
  }

  // @ts-ignore
  #getLink(item): string | string[] {
    if (item.url) {
      return item.url;
    } else if (item.contentPageLabelOrId) {
      return item.contentPageLabelOrId;
    } else if (item.categoryCode) {
      return this.semanticPathService.transform({
        cxRoute: 'category',
        params: {
          code: item.categoryCode,
          name: item.name
        }
      });
    } else if (item.productCode) {
      return this.semanticPathService.transform({
        cxRoute: 'product',
        params: {
          code: item.productCode,
          name: item.name
        }
      });
    }
    return '';
  }

  #getUpdatedNavigationTree(data: NavigationNode, categories: NavigationNode[]): NavigationNode {
    if (data.uid === 'DormerPrametMainNavNode' && data.children?.length) {
      return {
        ...data,
        children: data.children.map((child) =>
          child.uid === 'ProductsNavNode' && child.children?.length
            ? { ...child, children: [...child.children, ...categories] }
            : { ...child }
        )
      };
    }
    return { ...data };
  }
}

/* eslint-enable */
